import { useMemo, useState } from 'react';

import { entityName, hierarchyRows } from '../../lib/scene-doc.js';
import { usePrefabs } from '../../state/prefabs.js';
import { filesOfKind, useProjectFiles } from '../../state/project-files.js';
import { useScene } from '../../state/scene.js';
import { useSceneSet, type AdditiveScene } from '../../state/scene-set.js';
import { ContextMenu, MenuButton, type MenuItem } from '../ui/controls.js';
import { Icon } from '../ui/icons.js';

/**
 * Multi-scene parts of the Hierarchy: the active scene's header (save, add
 * scene), one collapsible section per additive scene (load / unload, set
 * active, save, remove) listing its entities, and the prefab-mode
 * breadcrumb.
 */

const sceneName = (path: string) => path.replace(/^.*\//, '').replace(/\.scene\.json$|\.prefab\.json$/, '');

function addSceneItems(): MenuItem[] {
  const active = useScene.getState().path;
  const added = new Set(useSceneSet.getState().scenes.map((s) => s.path));
  const scenes = filesOfKind(useProjectFiles.getState().entries, 'scene').filter((p) => p !== active && !added.has(p));
  if (scenes.length === 0) return [{ id: 'none', label: 'No other scenes in the project', disabled: true }];
  return [
    { id: 'h', label: 'Add scene (additive)', heading: true },
    ...scenes.map((p) => ({ id: p, label: p, icon: 'scene' as const, run: () => void useSceneSet.getState().add(p, true) })),
  ];
}

/** Header of the active scene (or the prefab being edited). */
export function ActiveSceneHeader() {
  const path = useScene((s) => s.path);
  const saveState = useScene((s) => s.saveState);
  const stage = usePrefabs((s) => s.stage);
  const additive = useSceneSet((s) => s.scenes.length);
  if (!path) return null;
  if (stage) {
    return (
      <div className="scene-section-head is-prefab-stage">
        <button type="button" className="tb-btn" title="Back to the scene" onClick={() => void usePrefabs.getState().closeStage()}>
          <Icon name="chevronRight" size={11} style={{ transform: 'rotate(180deg)' }} /> {stage.returnTo ? sceneName(stage.returnTo) : 'Back'}
        </button>
        <Icon name="prefab" size={13} />
        <span className="scene-section-name">{sceneName(stage.prefab)}</span>
        <span className="muted">{stage.mode === 'context' ? 'in context' : 'prefab mode'}</span>
        <span className={`scene-save is-${saveState}`}>{saveState === 'saved' ? '' : '•'}</span>
      </div>
    );
  }
  return (
    <div className="scene-section-head is-active">
      <Icon name="scene" size={13} />
      <span className="scene-section-name" title={path}>
        {sceneName(path)}
      </span>
      {additive > 0 && <span className="muted">active</span>}
      <span className={`scene-save is-${saveState}`} title={saveState}>
        {saveState === 'saved' ? '' : '•'}
      </span>
      <span className="tb-spacer" />
      <MenuButton
        className="ibtn"
        title="Scenes"
        items={() => [
          { id: 'save', label: 'Save Scene', icon: 'file', shortcut: 'Ctrl+S', run: () => void useScene.getState().flush() },
          { id: 'save-all', label: 'Save All Open Scenes', icon: 'file', run: () => void useSceneSet.getState().saveAll() },
          { id: 'sep', label: '', separator: true },
          ...addSceneItems(),
        ]}
      >
        <Icon name="more" size={14} />
      </MenuButton>
    </div>
  );
}

/** Sections of the additive scenes, below the active scene's tree. */
export function AdditiveSceneSections() {
  const scenes = useSceneSet((s) => s.scenes);
  const stage = usePrefabs((s) => s.stage);
  if (stage || scenes.length === 0) return null;
  return (
    <div className="scene-sections">
      {scenes.map((scene) => (
        <AdditiveSection key={scene.path} scene={scene} />
      ))}
    </div>
  );
}

function AdditiveSection({ scene }: { scene: AdditiveScene }) {
  const selected = useSceneSet((s) => s.selected);
  const [open, setOpen] = useState(true);
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);
  const rows = useMemo(() => (scene.doc ? hierarchyRows(scene.doc) : []), [scene.doc]);
  const set = useSceneSet.getState();
  const items: MenuItem[] = [
    { id: 'active', label: 'Set Active Scene', icon: 'scene', disabled: !scene.loaded, run: () => void set.setActive(scene.path) },
    { id: 'load', label: scene.loaded ? 'Unload Scene' : 'Load Scene', icon: scene.loaded ? 'eyeOff' : 'eye', run: () => void set.setLoaded(scene.path, !scene.loaded) },
    { id: 'save', label: 'Save Scene', icon: 'file', disabled: scene.saveState === 'saved', run: () => void set.save(scene.path) },
    { id: 'sep', label: '', separator: true },
    { id: 'remove', label: 'Remove Scene', icon: 'x', run: () => void set.remove(scene.path) },
  ];
  return (
    <div className={`scene-section${scene.loaded ? '' : ' is-unloaded'}`}>
      <div
        className="scene-section-head"
        onContextMenu={(e) => {
          e.preventDefault();
          setMenu({ x: e.clientX, y: e.clientY });
        }}
        onDoubleClick={() => scene.loaded && void set.setActive(scene.path)}
        title={scene.error ?? `${scene.path} — double-click to make it the active scene`}
      >
        <button type="button" className="tree-caret" onClick={() => setOpen((v) => !v)} disabled={!scene.loaded}>
          <Icon name={open && scene.loaded ? 'chevronDown' : 'chevronRight'} size={11} />
        </button>
        <Icon name="scene" size={13} />
        <span className="scene-section-name">{sceneName(scene.path)}</span>
        {!scene.loaded && <span className="muted">(not loaded)</span>}
        {scene.error && <Icon name="warning" size={12} />}
        <span className={`scene-save is-${scene.saveState}`}>{scene.saveState === 'saved' ? '' : '•'}</span>
        <span className="tb-spacer" />
        <MenuButton className="ibtn" title="Scene options" items={items}>
          <Icon name="more" size={14} />
        </MenuButton>
      </div>
      {open && scene.loaded && (
        <ul className="tree-list scene-section-tree" role="group">
          {rows.map(({ entity, depth }) => {
            const isSel = selected?.path === scene.path && selected.id === entity.id;
            return (
              <li
                key={entity.id}
                className={`tree-item is-additive${isSel ? ' is-selected' : ''}`}
                style={{ paddingLeft: 20 + depth * 14 }}
                onClick={() => set.select({ path: scene.path, id: entity.id })}
                title={entity.id}
              >
                <span className="tree-label">{entityName(entity)}</span>
              </li>
            );
          })}
          {rows.length === 0 && <li className="tree-empty muted">Empty scene.</li>}
        </ul>
      )}
      {menu && <ContextMenu x={menu.x} y={menu.y} items={items} onClose={() => setMenu(null)} />}
    </div>
  );
}
